import React, { useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader } from '@zxing/browser';
import { X, Camera } from 'lucide-react';

interface QRScannerProps {
  onScan: (result: string) => void;
  onClose: () => void;
}

export function QRScanner({ onScan, onClose }: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);

  useEffect(() => {
    const codeReader = new BrowserQRCodeReader();
    
    const startScanning = async () => {
      try {
        const videoInputDevices = await BrowserQRCodeReader.listVideoInputDevices();
        
        if (videoInputDevices.length === 0) {
          setError('No camera found on this device');
          return;
        }

        // Prefer back camera on mobile
        const backCamera = videoInputDevices.find(
          device => device.label.toLowerCase().includes('back')
        );
        const deviceId = backCamera?.deviceId || videoInputDevices[0].deviceId;

        const controls = await codeReader.decodeFromVideoDevice(
          deviceId,
          videoRef.current!,
          (result) => {
            if (result) {
              const url = result.getText();
              if (url.includes('gallery-g') || url.includes('localhost')) {
                controls.stop();
                onScan(url);
              }
            }
          }
        );
        
        controlsRef.current = controls;
      } catch (err) {
        setError('Unable to access camera. Please grant camera permissions.');
      }
    };

    startScanning();

    return () => {
      controlsRef.current?.stop();
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 flex justify-between items-center">
        <span className="text-white font-medium">Scan QR Code</span>
        <button
          onClick={() => {
            controlsRef.current?.stop();
            onClose();
          }}
          className="p-2 rounded-full bg-black/50 text-white"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Scanner */}
      <div className="h-full flex items-center justify-center">
        {error ? (
          <div className="text-center p-6">
            <Camera className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-white mb-2">{error}</p>
            <button
              onClick={onClose}
              className="text-gallery-accent underline"
            >
              Go back
            </button>
          </div>
        ) : (
          <div className="relative w-full max-w-sm aspect-square">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
            />
            {/* Scanning frame overlay */}
            <div className="absolute inset-8 border-2 border-gallery-accent rounded-2xl">
              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-gallery-accent rounded-tl-lg" />
              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-gallery-accent rounded-tr-lg" />
              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-gallery-accent rounded-bl-lg" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-gallery-accent rounded-br-lg" />
            </div>
          </div>
        )}
      </div>

      {/* Footer instruction */}
      <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
        <p className="text-gray-400">
          Point your camera at the QR code near the artwork
        </p>
      </div>
    </div>
  );
}
